import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useMemo, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { GridCheckbox } from '~/components/GridCheckbox';
import { SelectableGridItem } from '~/components/cards/styles';
import { GridCard } from '~/components/media/card';
import { useLink } from '~/hooks/link';
import type {
  PlaylistList__Playlist as Playlist,
  PlaylistList__PlaylistFolder as PlaylistFolder,
} from '../../queries/queries.generated';
import { PlaylistCard } from './PlaylistCard';
import { PlaylistFolderCard } from './PlaylistFolderCard';
import { Menu } from './PlaylistMenu';

interface PlaylistGridItemProps {
  item: PlaylistFolder | Playlist;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
}

export const PlaylistGridItem = ({ item, onCheck, selectedIds }: PlaylistGridItemProps) => {
  const { t } = useTranslation();
  const link = useLink();
  const [searchParams] = useSearchParams();

  const selectionId = useMemo(
    () => (item.__typename === 'Playlist' ? item.id : `PlaylistFolder-${item.id}`),
    [item],
  );
  const selected = selectedIds.includes(selectionId);

  return (
    <SelectableGridItem item xs={12} sm={6} md={3} lg={2} key={selectionId} selected={selected}>
      <GridCard>
        {item.__typename === 'PlaylistFolder' ? (
          <PlaylistFolderCard playlistFolder={item} />
        ) : (
          <>
            <GridCheckbox
              checked={selected}
              inputProps={{ 'aria-label': t('common.actions.select_item', { name: item.name }) }}
              onChange={(event) => {
                if (event.target.checked) {
                  return onCheck([...selectedIds, selectionId]);
                }
                onCheck(selectedIds.filter((x) => x !== selectionId));
              }}
            />
            <PlaylistCard
              playlist={item}
              to={{
                ...link(`/playlists/${item.playlistFolderId}/playlist/${item.id}/design`),
                search: searchParams.toString(),
              }}
            >
              <Menu item={item} />
            </PlaylistCard>
          </>
        )}
      </GridCard>
    </SelectableGridItem>
  );
};
